import React, { useEffect, useState } from 'react';
import { Button, Stack, Image } from 'react-bootstrap';
import OverviewBox from 'components/OverviewBox';
import useAuthStore, { setUserInfo, updateUserInfo } from 'store/auth';
import { useCompanyDetailsQuery, usePostedJobQuery } from 'services';
import { setProfileData } from 'store/user';
import { HeaderTitle } from 'components/UserProfile';
import useTranslate from 'hooks/useTranslate';
import { updateCompanyAuthValues } from 'utils';
import parse from 'html-react-parser';
import { IMAGE_PATHS } from 'constants/imagePaths';
import { dataLimits, USER_TYPES } from 'constants';
import JobListingBox from 'components/Common/Jobs/JobListingBox';
import { useNavigate, useParams } from 'react-router-dom';
import { setModalConfig } from 'store/common';
import useSkeleton from 'hooks/useSkeleton';
import ProfileSkeleton from 'components/Common/Skeleton/ProfileSkeleton';
import './styles.scss';
import useLocalizationStore from 'store/localization';

const CompanyProfile: React.FC = () => {
	const navigate = useNavigate();
	const { translate } = useTranslate();
	const [isSelf, setIsSelf] = useState(false);
	const { token, user }: Record<string, any> = useAuthStore((state) => state.userInfo ?? {});
	const { activeLanguage }: any = useLocalizationStore((state) => state);
	const { id: companyId = '' } = useParams<any>();
	const { data: { userDetails = {}, followers, followings, employees, avgRating } = {}, refetch } =
		useCompanyDetailsQuery(companyId ? companyId : user?.userId);

	const queryOptions = `limit=${dataLimits?.postedJobs}&page=1&orderBy=popular&order=DESC&userId=${companyId}`;
	const { data: { docs: jobs = [], meta = {} } = [] } = usePostedJobQuery(queryOptions);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const delayedLoading = useSkeleton(isLoading);

	useEffect(() => {
		setIsLoading(true);
		refetch().finally(() => {
			setIsLoading(false);
		});
	}, [companyId, activeLanguage]);

	useEffect(() => {
		setIsLoading(true);
		refetch().finally(() => {
			setIsLoading(false);
		});
	}, []);

	const onHideModal = () => {
		setModalConfig({
			visible: false,
			id: null,
			type: '',
		});
		refetch();
	};

	const onShowModal = (type: string, id: number | null = null) => {
		setModalConfig({
			visible: true,
			id,
			type,
			onClose: onHideModal,
			data: {
				showProfileDelIcon: companyDetails?.profilePicture.length > 0,
				showCoverDelIcon: companyDetails?.coverPicture.length > 0,
			},
		});
	};

	useEffect(() => {
		if (userDetails?.id) {
			if ((user?.userId && !companyId) || Number(companyId) === user?.userId) {
				setIsSelf(true);
				setUserInfo({
					token: token,
					user: {
						...user,
						...updateCompanyAuthValues(userDetails ?? {}),
					},
				});
				updateUserInfo({
					employees: employees || 0,
					followers: followers || 0,
					followings: followings || 0,
				});
			} else {
				setIsSelf(false);
			}
			setProfileData(userDetails);
		}
	}, [userDetails]);

	const companyDetails = {
		coverPicture: userDetails?.coverPicture,
		profilePicture: userDetails?.profilePicture,
		firstName: userDetails?.companyName,
		designation: userDetails?.industryName,
		address: userDetails?.address,
		followers: followers,
		followings: followings,
		employees: employees,
		avgRating: avgRating,
		userType: 'company',
		companyDetails: userDetails,
	};

	if (delayedLoading) {
		return <ProfileSkeleton />;
	}
	if (companyDetails?.companyDetails?.isBlocked) {
		navigate('/social-feed-posts');
	}

	return (
		<section className="company-profile">
			<div className="d-flex flex-column flex-lg-row gap-4">
				<div className="right-area flex-1 d-flex flex-column">
					<OverviewBox
						isSelf={isSelf}
						data={companyDetails}
						onShowModal={onShowModal}
						companyDetails={companyDetails.companyDetails}
						receiverId={companyId}
					/>
					<div className="about-box white-box bg-white">
						<HeaderTitle
							title={translate('Profile.editAboutCompany')}
							type="editCompanyAboutUs"
							onShowModal={onShowModal}
							isEdit={true}
							isSelf={isSelf}
						/>

						{userDetails?.companyDescription && (
							<div className="about-box-content">
								<p className="mb-0">
									{parse(userDetails.companyDescription.replace(/\n/g, '<br>'))}
								</p>
							</div>
						)}

						<div className="about-company d-flex flex-wrap">
							{userDetails?.companyCEOName && (
								<div className="about-company-box d-flex align-items-center">
									<Image src={IMAGE_PATHS.UserIcon} loading="lazy" />
									<div>
										<h5 className="mb-1 fw-semibold">{userDetails.companyCEOName}</h5>
										<p className="mb-0">{translate('Company.ceo')}</p>
									</div>
								</div>
							)}

							{userDetails?.companyFoundedYear > 0 && (
								<div className="about-company-box d-flex align-items-center">
									<Image src={IMAGE_PATHS.FoundedIcon} loading="lazy" />
									<div>
										<h5 className="mb-1 fw-semibold">{translate('Company.founded')}</h5>
										<p className="mb-0">{userDetails.companyFoundedYear}</p>
									</div>
								</div>
							)}

							{userDetails?.companySize && (
								<div className="about-company-box d-flex align-items-center">
									<Image src={IMAGE_PATHS.CompanyIcon} loading="lazy" />
									<div>
										<h5 className="mb-1 fw-semibold">{translate('Company.CompanySize')}</h5>
										<p className="mb-0">
											{userDetails.companySize} {translate('Company.employee')}
										</p>
									</div>
								</div>
							)}
						</div>
					</div>

					<div className="posted-jobs white-box bg-white">
						<div className="white-box-heading d-flex align-items-center justify-content-between">
							<h3 className="fw-bold mb-0">{translate('Job.postedJobs')}</h3>
							{meta?.totalPages > 1 && (
								<Button
									className="white-box-btn transparent-btn rounded-pill lh-1"
									onClick={() =>
										navigate(isSelf ? `/company/my-jobs` : `/company/jobs/${companyId}`)
									}
								>
									{translate('Company.viewMore')}
								</Button>
							)}
						</div>
						<Stack className="posted-jobs-list">
							{jobs?.length > 0 ? (
								jobs.map((job: any, index: number) => (
									<JobListingBox
										job={job}
										key={index}
										enableFavBtn={!isSelf && user?.userType === USER_TYPES?.SEEKER}
										enableApplyBtn={!isSelf && user?.userType === USER_TYPES?.SEEKER}
										enableAddress={false}
										enableKebabIcon={isSelf}
										refetch={refetch}
									/>
								))
							) : (
								<Stack className="posted-job-box">
									<p className="mb-0">{translate('Common.noJobsFound')} </p>
								</Stack>
							)}
						</Stack>
					</div>
				</div>
			</div>
		</section>
	);
};

export default CompanyProfile;
